import { type JSX, useCallback, useEffect, useState } from "react";
import {
  useGetFeatureFlagsByUserId,
  useGetUsers,
} from "@via/pages/account/admin/admin.api.tsx";
import { SelectOption } from "@via/utils/interfaces.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import FeatureFlagsTable from "@via/pages/account/admin/feature-flags/feature-flags-table.tsx";
import { useDebounceCallback } from "usehooks-ts";
import { User } from "common";
import SelectComponent from "@via/components/select/select";
import { FilterItem } from "@via/pages/account/admin/utils/helper.ts";

export default function IndividualFeatureFlags(): JSX.Element {
  const [selectedUserId, setSelectedUserId] = useState<number>();
  const [searchedInput, setSearchedInput] = useState("");

  const [cumulativeUsers, setCumulativeUsers] = useState<User[]>([]);
  const [usersPage, setUsersPage] = useState(0);
  const userOptionSize = 10;

  const userFilters = [
    {
      id: "username",
      value: { operator: "like", term: searchedInput || "" },
    } as FilterItem,
  ];

  const { data: users, isLoading: isUsersLoading } = useGetUsers({
    take: userOptionSize,
    skip: usersPage * userOptionSize,
    filter: userFilters,
  });

  const fetchMoreUsers = useCallback(() => {
    setUsersPage(currentPage => {
      const nextPage = currentPage + 1;
      const maxPage = Math.ceil((users?.total ?? 0) / userOptionSize) - 1;
      return nextPage <= maxPage ? nextPage : currentPage;
    });
  }, [users?.total, userOptionSize]);

  useEffect(() => {
    if (users?.data) {
      setCumulativeUsers([...cumulativeUsers, ...users.data]);
    }
  }, [users?.data]);

  const {
    data: featureFlags,
    mutateAsync: getFeatureFlagsByUserId,
    isPending: isGetFeatureFlagsByUserIdLoading,
    isSuccess: isGetFeatureFlagsByUserIdSuccess,
  } = useGetFeatureFlagsByUserId();

  const handleUserChange = async (selectedOption: SelectOption) => {
    if (selectedOption) {
      await getFeatureFlagsByUserId({
        userId: Number(selectedOption.value),
      });

      setSelectedUserId(Number(selectedOption.value));
    }
  };

  const debouncedHandleInputChange = useDebounceCallback((value: string) => {
    if (value === searchedInput) return;
    setCumulativeUsers([]);
    setUsersPage(0);
    setSearchedInput(value);
  }, 300);

  const handleFlagsSave = async () => {
    selectedUserId &&
      (await getFeatureFlagsByUserId({
        userId: selectedUserId,
      }));
  };

  return (
    <div>
      <form>
        <div>
          <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
            Choose User<span className="text-red-400">*</span>
          </label>
          <div>
            <SelectComponent
              name="userId"
              options={
                cumulativeUsers?.map(({ userName, id }) => ({
                  label: userName,
                  value: id,
                })) ?? []
              }
              isLoading={isUsersLoading}
              placeholder={"Enter Username"}
              onFetchMoreOptions={fetchMoreUsers}
              onChange={data => {
                handleUserChange(data as SelectOption);
              }}
              onInputChange={inputValue =>
                debouncedHandleInputChange(inputValue)
              }
            />
          </div>
        </div>
      </form>

      <div className="mt-2">
        {isGetFeatureFlagsByUserIdLoading ? (
          <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
            <SkeletonLoader count={12} />
          </div>
        ) : isGetFeatureFlagsByUserIdSuccess ? (
          <div>
            {
              <FeatureFlagsTable
                data={featureFlags}
                selectedUserId={selectedUserId}
                onFlagsSave={handleFlagsSave}
              />
            }
          </div>
        ) : null}
      </div>
    </div>
  );
}
